import React from 'react'
import {
  useDealFreezeTagTracking,
  useIsFreezableTopOffer
} from 'components/Accommodation/Offers/DealFreezeOfferBox'
import {SplitBookingOfferInfo} from 'components/Offer/types'

import {Box} from '@vio/atlas/helpers/Box'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {OfferLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {OfferSecondaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {FreezableDealTag} from '@vio/shared/src/dealFreeze/components/DealFreezeOfferBox'

import {OfferLogo} from '../OfferLogo'
import {shouldOfferBeBranded} from '../utils'

type ProviderLogoVariants = Exclude<
  OfferLayoutVariant,
  OfferSecondaryLayoutVariant
>

interface MainProps {
  hotelId: string
  variant: ProviderLogoVariants
  offer: SearchOffer
  isAppLockedDeal: boolean
  sourceComponent?: string
  hasVioTag?: boolean
  isSplitBooking?: false
  splitBookingOffers?: never[]
}

interface SplitBookingProps {
  hotelId: string
  variant: ProviderLogoVariants
  isSplitBooking: true
  isAppLockedDeal?: never
  sourceComponent?: never
  hasVioTag?: never
  splitBookingOffers: SplitBookingOfferInfo[]
  offer?: never
}

export const ProviderLogoComponent: React.FC<MainProps | SplitBookingProps> = ({
  hotelId,
  isSplitBooking = false,
  splitBookingOffers = [],
  offer,
  variant,
  isAppLockedDeal,
  sourceComponent,
  hasVioTag = false
}) => {
  const {brandCode} = useBrand()
  const {
    monetisation: {hasBrandedOffers},
    commerce: {bookOnBrand}
  } = useBrandCapabilities()
  const isBookOnBrand = bookOnBrand.enabled
  const isCompact = variant.startsWith('Compact')
  // The "Only on Vio" tag takes the slot, one tag per row
  const showFreezableTag = useIsFreezableTopOffer(offer?.id) && !hasVioTag
  const {onDisplayed, onClicked} = useDealFreezeTagTracking({
    offerId: showFreezableTag ? offer?.id : undefined,
    sourceComponent
  })

  if (isSplitBooking) {
    const firstOffer = splitBookingOffers[0]?.offer
    const secondOffer = splitBookingOffers[1]?.offer

    const isSameProvider =
      firstOffer.intermediaryProvider === secondOffer.intermediaryProvider

    const isFirstOfferBranded = shouldOfferBeBranded({
      offerIntermediaryProviderCode: firstOffer?.intermediaryProvider,
      isBookOnBrand,
      hasBrandedOffers
    })

    const isSecondOfferBranded = shouldOfferBeBranded({
      offerIntermediaryProviderCode: secondOffer?.intermediaryProvider,
      isBookOnBrand,
      hasBrandedOffers
    })

    return (
      <Box
        data-id="Logo"
        display="flex"
        flexDirection="column"
        gap="s200"
        marginTop="s200"
        marginBottom={variant !== 'PrimaryDefault' ? 's200' : '0'}
      >
        <OfferLogo
          hotelId={hotelId}
          providerCodeOverride={
            (isAppLockedDeal || isFirstOfferBranded) && brandCode
          }
          offer={firstOffer}
          isSplitBooking
          isPrimaryOffer
          isAppLockedDeal={isAppLockedDeal}
        />
        {!isSameProvider && (
          <OfferLogo
            hotelId={hotelId}
            offer={secondOffer}
            isSplitBooking
            isPrimaryOffer
            providerCodeOverride={isSecondOfferBranded && brandCode}
          />
        )}
      </Box>
    )
  }

  const {intermediaryProvider} = offer || {}

  const isBrandedDeal = shouldOfferBeBranded({
    offerIntermediaryProviderCode: intermediaryProvider,
    isBookOnBrand,
    hasBrandedOffers
  })

  const offerLogo = (
    <OfferLogo
      hotelId={hotelId}
      offer={offer}
      isPrimaryOffer
      providerCodeOverride={(isAppLockedDeal || isBrandedDeal) && brandCode}
      isAppLockedDeal={isAppLockedDeal}
    />
  )

  return (
    <Box
      data-id="Logo"
      display="flex"
      flexDirection="column"
      gap="s200"
      marginTop="s200"
      marginBottom={variant !== 'PrimaryDefault' ? 's200' : '0'}
    >
      {showFreezableTag ? (
        <Box
          display="flex"
          alignItems="center"
          gap={isCompact ? 's300' : 's250'}
          flexWrap="wrap"
        >
          {offerLogo}
          <FreezableDealTag
            size={isCompact ? 'xs' : 'sm'}
            onDisplayed={onDisplayed}
            onClick={onClicked}
          />
        </Box>
      ) : (
        offerLogo
      )}
    </Box>
  )
}
